import Parse from 'parse';
import { TableDataSource, FieldFilter, FilterCondition } from '../table.component';
import { Observable, BehaviorSubject } from 'rxjs';
import { Sort } from '@angular/material/sort';
import { PFObject } from 'app/components/data/core/models/base/object';

export class LocalDataSource implements TableDataSource {
    data = new BehaviorSubject<Parse.Object[]>([]);
    dataCustomers = new BehaviorSubject<Parse.Object[]>([]);
    dataAuthors = new BehaviorSubject<Parse.Object[]>([]);
    dataSuppliers = new BehaviorSubject<Parse.Object[]>([]);

    private localData: Parse.Object[];
    private filteredData: Parse.Object[];

    private filter: string;
    private otherFilters: FieldFilter[] = [];
    private treeNodeFilters: FieldFilter[] = [];
    private filterFn: (obj: Parse.Object, filter: string) => boolean;

    private lastOffset: number = 0;
    private lastCount: number = 0;

    private defaultSort: Sort = null;
    private sort: Sort = null;

    constructor(data: Parse.Object[], filterFn: (obj: Parse.Object, filter: string) => boolean = null) {
        this.localData = data;
        this.filteredData = data;
        this.filterFn = filterFn;
    }

    connect(): Observable<Parse.Object[]> {
        return this.data.asObservable();
    }
    disconnect(): void {
        this.data.complete();
    }
    hasData(): boolean {
        let value = this.getData();
        return value && value.length > 0;
    }

    totalDataCount(): number {
        if (this.hasData()) {
            return this.filteredData.length;
        }
        return 0;
    }

    loadAllCustomers(): void {}
    loadUniqueAuthors(): void {}
    loadAllSuppliers(): void {}

    loadData(offset: number, count: number) {
        this.lastOffset = offset;
        this.lastCount = count;

        let data = [];
        if (count > 0) {
            if (offset < this.filteredData.length) {
                data = this.filteredData.slice(offset, offset + count);
            }
        } else {
            data = this.filteredData;
        }

        let currentSort;
        if (this.sort && this.sort.active !== undefined && this.sort.direction != '') {
            currentSort = this.sort;
        } else if (this.defaultSort) {
            currentSort = this.defaultSort;
        }
        
        if (data) {
            if (currentSort) {
                data = this.sortData(data, currentSort);
            }
        } 
        this.data.next(data);
    }

    getData() {
        return this.data.getValue();
    }

    refresh() {
        this.data.next(this.getData());
    }

    setSearchFilter(value: string) {
        this.filter = value;
        this.filterData();
    }

    setOtherFilters(filters: FieldFilter[]) {
        this.otherFilters = filters || [];
        this.filterData();
    }

    setNodeTreeFilters(filters: FieldFilter[]) {
        this.treeNodeFilters = filters || [];
        this.filterData();
    }

    isLoading() : boolean {
        return false;
    }

    getLastLoadError(): string {
        return null;
    }

    setDefaultSort(sort: Sort) {
        this.defaultSort = sort;
    }

    setSort(sort: Sort) {
        this.sort = sort;
    }

    setLocalData(data: Parse.Object[]) {
        this.localData = data || [];
        this.filterData();
        this.loadData(this.lastOffset, this.lastCount);
    }

    // Private

    private filterData() {
        let data = this.localData || [];
        if (this.filterFn && this.filter && this.filter.trim().length > 0) {
            data = data.filter(obj => this.filterFn(obj, this.filter));
        }
        if (this.otherFilters && this.otherFilters.length > 0) {
            data = data.filter(obj => this.matchesOtherFilters(obj));
        }
        this.filteredData = data;
    }

    private matchesOtherFilters(object: PFObject): boolean {
        for (let filter of this.otherFilters) {
            if (!this.matchesFilterCondition(object, filter)) {
                return false;
            }
        }
        return true;
    }

    private matchesFilterCondition(object: PFObject, filter: FieldFilter): boolean {
        const value = PFObject.valueAtPath(object as PFObject, filter.field);
        switch (filter.condition) {
            case FilterCondition.Equal:
                return this.valuesEqual(value, filter.value);
            case FilterCondition.In:
                return this.valuesContainedIn(value, filter.value);
            case FilterCondition.GreaterThan:
                return this.compareValues(value, filter.value) > 0;
            case FilterCondition.GreaterThanOrEqual:
                return this.compareValues(value, filter.value) >= 0;
            case FilterCondition.LessThan:
                return this.compareValues(value, filter.value) < 0;
            case FilterCondition.LessThanOrEqual:
                return this.compareValues(value, filter.value) <= 0;
            default:
                return true;
        }
    }

    private valuesEqual(a: any, b: any): boolean {
        if (a instanceof Parse.Object || b instanceof Parse.Object) {
            return PFObject.compareFn(a, b);
        }
        return a === b;
    }

    private valuesContainedIn(value: any, list: any[]): boolean {
        if (!Array.isArray(list)) return false;
        if (value instanceof Parse.Object) {
            return list.some(item => PFObject.compareFn(value, item));
        }
        return list.includes(value);
    }

    private compareValues(a: any, b: any): number {
        const left = a instanceof Date ? a.getTime() : a;
        const right = b instanceof Date ? b.getTime() : b;
        if (left == null && right == null) return 0;
        if (left == null) return -1;
        if (right == null) return 1;
        if (left > right) return 1;
        if (left < right) return -1;
        return 0;
    }

    private sortData(data: any[], sort: Sort) {
        let directionModifier;
        switch (sort.direction) {
            case 'asc': directionModifier = 1; break;
            case 'desc': directionModifier = -1; break;
            default: directionModifier = 0; break;
        }
        
        return data.sort((o1, o2) => {
            let val1 = PFObject.valueAtPath(o1, sort.active);
            let val2 = PFObject.valueAtPath(o2, sort.active);
            var res = 0;
            if (val1 == val2) {
                res = 0;
            } else if (val1 && val2) {
                if (val1 < val2) {
                    res = -1;
                }
                if (val2 < val1) {
                    res = 1;
                }
            } else  {
                if (!val1) {
                    res = -1;
                } else {
                    res = 1;
                }
            }
            return res * directionModifier;
        })
    }
}